import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";
import { invoiceService } from "@src/@api-services/invoice";
import type { GetInvoicesParams } from "@src/@api-services/invoice";
import { TableSort } from "@src/shared/constants/sort-order.constants";
import type { Invoice } from "@src/views/apps/financial/types";

interface InvoicesState {
  data: Invoice[];
  total: number;
  params: GetInvoicesParams;
  selectedInvoice: Invoice | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

type InvoiceApiRow = Omit<Invoice, "id" | "transactionId"> & {
  id: number;
  transactionId: number;
};

const mapInvoiceRow = ({ id, transactionId, ...rest }: InvoiceApiRow): Invoice => ({
  ...rest,
  id: String(id),
  transactionId: String(transactionId),
});

const getErrorMessage = (error: unknown, fallback: string) =>
  (isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  fallback;

export const getInvoices = createAsyncThunk(
  "invoices/getInvoices",
  async (params: GetInvoicesParams, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoices(params);
      const payload = response.data?.data;
      const rows: InvoiceApiRow[] = payload?.data ?? [];
      return {
        params,
        data: rows.map(mapInvoiceRow),
        total: Number(payload?.totalRecord ?? rows.length),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch invoices"));
    }
  }
);

export const getInvoice = createAsyncThunk(
  "invoices/getInvoice",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoice(id);
      const row: InvoiceApiRow | undefined = response.data?.data;
      return row ? mapInvoiceRow(row) : null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch invoice"));
    }
  }
);

const initialState: InvoicesState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "issuedAt",
    orderDirection: TableSort.DESC,
  },
  selectedInvoice: null,
  loading: false,
  detailLoading: false,
  error: null,
};

export const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedInvoice: (state) => {
      state.selectedInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getInvoice.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedInvoice = action.payload;
      })
      .addCase(getInvoice.rejected, (state) => {
        state.detailLoading = false;
        state.selectedInvoice = null;
      });
  },
});

export const { clearError, clearSelectedInvoice } = invoicesSlice.actions;
export default invoicesSlice.reducer;
